import React, {useState, useMemo, useEffect, useCallback, useRef} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Tabs, Card, Tag, NavBar, Skeleton, ImageViewer, ErrorBlock, Button} from 'antd-mobile';
import {LeftOutline, RightOutline} from 'antd-mobile-icons';
import {useQuery, useApolloClient} from '@apollo/client';
import {BarCodeScanner} from "../../../components/BarCodeScanner";
import {useNetwork} from "../../../hooks/useNetwork";
import {
    GET_DSP_DRIVER_PACKAGES,
    DspDriverPackagesResponse,
    DspPackageData
} from '../graphql/queries/dspOperationalData';
import useDspWarehouseStore from '../store/dspWarehouseStore';
import {formatTimestampToSecondsTime} from '../../../utils/dateTimeUtils';
import DspSearchBar from "../components/DspSearchBar";
import {t} from "../../../i18n";
import {DspPath} from "../../../enums/DeliveryNavigatePathEnum";
import {useLazyQuery} from '@apollo/client';
import {
    GET_IMAGE_URLS_BY_S3_KEYS,
    GetImageUrlsByS3KeysResponse,
    GetImageUrlsByS3KeysVariables,
    GET_DSP_PACKAGE_DETAIL,
    GetDspPackageDetailByTrackingNumberResponse,
    GetDspPackageDetailByTrackingNumberVariables
} from '../graphql/queries/getPackageDetail';
import {getExceptionDictionaryAll} from '../../delivery/graphql/queries/getExceptionDictionaryForDsp';
import {ExceptionDictionaryType} from '../../delivery/graphql/queries/getExceptionDictionaryForDelivery';
import {getCurrentLanguage} from '../../../utils/i18nUtils';

// 包裹状态枚举
enum PackageStatus {
    DELIVERING = 'DELIVERING',
    DELIVERED = 'DELIVERED',
    FAILED = 'FAILED',
    RETURNED = 'RETURNED'
}

// 本地包裹数据接口（扩展GraphQL类型）
interface LocalPackageData extends Omit<DspPackageData, 'status'> {
    id: string;
    status: PackageStatus;
    driverId?: number | null;
    address?: string;
    deliveryTime?: string;
    failureReason?: string;
    operationTime?: string;
    podImageS3KeysJson?: string[];
    exceptionImageS3KeysJson?: string[];
    deliveryAttempts: number;
    orderNote?: {
        id: number;
        note: string;
        lang: string;
        orderNoteTranslations: Array<{
            lang: string;
            translateNote: string;
        }>;
    };
}

// Tab配置接口
interface TabConfig {
    key: PackageStatus;
    title: string;
    count: number;
}


// 包裹卡片组件
const PackageCard: React.FC<{ 
    package: LocalPackageData; 
    onViewDetail: (trackingNumber: string) => void;
    onViewProof?: (trackingNumber: string, proofType: 'delivery' | 'failure') => void;
    getExceptionReasonText: (code: string | undefined) => string;
    loadingImages?: boolean;
}> = ({
    package: pkg,
    onViewDetail,
    onViewProof,
    getExceptionReasonText,
    loadingImages
}) => {

    const handleCardClick = () => {
        onViewDetail(pkg.trackingNumber);
    };

    const handleViewProofClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onViewProof) {
            onViewProof(
                pkg.trackingNumber, 
                pkg.status === PackageStatus.DELIVERED ? 'delivery' : 'failure'
            );
        }
    };

    const handleArrowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onViewDetail(pkg.trackingNumber);
    };

    return (
        <Card 
            className="rounded-lg shadow-sm mb-3 cursor-pointer active:opacity-70"
            onClick={handleCardClick}
        >
            <div className="p-4">
                <div className="flex items-center justify-between">
                    {/* 左侧信息区域 */}
                    <div className="flex-1">
                        <div className="space-y-2">
                            <div className="flex items-center">
                                <span className="text-sm">{t('pages.dsp.operationalDataParticulars.trackingNumber')}：</span>
                                <span className="text-sm font-medium">{pkg.trackingNumber}</span>
                            </div>

                            {pkg.status !== PackageStatus.RETURNED && (
                                <div className="flex items-center">
                                    <span className="text-sm">{t('pages.dsp.operationalDataParticulars.driver')}：</span>
                                    <span className="text-sm ml-1">
                                        {pkg.driverName && pkg.driverName.trim()
                                            ? pkg.driverName
                                            : (pkg.deliveryDriverId ? String(pkg.deliveryDriverId) : '--')}
                                    </span>
                                </div>
                            )}
                            {pkg.status !== PackageStatus.RETURNED && (
                                <div className="flex items-center">
                                    <span className="text-sm">{t('pages.dsp.operationalDataParticulars.taskId')}：</span>
                                    <span className="text-sm ml-1">{pkg.taskNumber}</span>
                                </div>
                            )}

                            {pkg.status === PackageStatus.DELIVERED && pkg.deliveryTime && (
                                <div className="flex items-center">
                                    <span className="text-sm">{t('pages.dsp.operationalDataParticulars.deliveryAt')}：</span>
                                    <span className="text-sm">{pkg.deliveryTime}</span>
                                </div>
                            )}

                            {pkg.status === PackageStatus.FAILED && (
                                <div className="flex items-center">
                                    <span className="text-sm">{t('pages.dsp.operationalDataParticulars.exceptionReason')}：</span>
                                    <span className="text-sm text-purple-600 ml-1">{pkg.exceptionReasonCode ? getExceptionReasonText(pkg.exceptionReasonCode) : t('pages.dsp.operationalDataParticulars.notAvailable')}</span>
                                </div>
                            )}
                            {pkg.status === PackageStatus.FAILED && pkg.operationTime && (
                                <div className="flex items-center">
                                    <span className="text-sm">{t('pages.dsp.operationalDataParticulars.failAt')}：</span>
                                    <span className="text-sm ml-1">{pkg.operationTime || t('common.notAvailable', '暂无')}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 右侧箭头图标 */}
                    <div className="ml-4 flex-shrink-0">
                        <RightOutline
                            className="text-gray-400 cursor-pointer"
                            onClick={handleArrowClick}
                        />
                    </div>
                </div>

                {/* 查看凭证按钮区域 */}
                {(pkg.status === PackageStatus.DELIVERED || pkg.status === PackageStatus.FAILED) && onViewProof && (
                    <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
                        <button
                            className="py-2 px-4 bg-purple-600 text-white text-sm font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleViewProofClick}
                            disabled={loadingImages}
                        >
                            {loadingImages ? t('pages.dsp.operationalDataParticulars.loading') : (pkg.status === PackageStatus.DELIVERED ? t('pages.dsp.operationalDataParticulars.viewPod') : t('pages.dsp.operationalDataParticulars.viewPof'))}
                        </button>
                    </div>
                )}
            </div>
        </Card>
    );
};

const Body: React.FC<{ driverId?: string; date: string | null }> = ({driverId, date}) => {
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const warehouseIdVar = selectedWarehouseId ? parseInt(selectedWarehouseId) : undefined;
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchValue, setSearchValue] = useState<string>('');
    const apolloClient = useApolloClient();

    // 从URL参数获取初始tab状态，如果没有则默认为DELIVERING
    const initialTab = (searchParams.get('tabType') as PackageStatus) || PackageStatus.DELIVERING;
    const [activeTab, setActiveTab] = useState<PackageStatus>(initialTab);
    
    // 图片查看器状态
    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [currentImages, setCurrentImages] = useState<string[]>([]);
    const [imageViewerKey, setImageViewerKey] = useState(0);
    const [loadingImages, setLoadingImages] = useState(false);
    const [currentProofType, setCurrentProofType] = useState<'delivery' | 'failure' | null>(null);

    // 异常原因字典状态
    const [exceptionDictMap, setExceptionDictMap] = useState<Map<string, ExceptionDictionaryType>>(new Map());

    // 页面首次加载时，如果URL中没有tabType参数，自动添加默认值DELIVERING
    useEffect(() => {
        const tabTypeFromUrl = searchParams.get('tabType');
        if (!tabTypeFromUrl) {
            // URL中没有tabType参数，添加默认值
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('tabType', PackageStatus.DELIVERING);
            setSearchParams(newSearchParams, { replace: true });
        }
    }, []); // 只在组件挂载时执行一次

    // 公共方法：显示图片查看器
    const showImages = useCallback((urls: string[]) => {
        const validUrls = urls.filter(url => url && url.trim() !== '');
        if (validUrls.length > 0) {
            setCurrentImages(validUrls);
            setImageViewerKey(prev => prev + 1);
            setImageViewerVisible(true);
        }
        setLoadingImages(false);
    }, []);

    // GraphQL查询：获取图片URLs
    const [getImageUrls] = useLazyQuery<GetImageUrlsByS3KeysResponse, GetImageUrlsByS3KeysVariables>(
        GET_IMAGE_URLS_BY_S3_KEYS,
        {
            onCompleted: (data) => {
                showImages(data.getImageUrlsByS3Keys);
            },
            onError: (error) => {
                console.error('Failed to load images:', error);
                setLoadingImages(false);
            }
        }
    );

    // GraphQL查询：获取包裹详情（用于获取图片URLs）
    const [getPackageDetail] = useLazyQuery<GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables>(
        GET_DSP_PACKAGE_DETAIL,
        {
            onCompleted: (data) => {
                const packageDetail = data.getDspPackageDetailByTrackingNumber;
                if (!packageDetail) {
                    setLoadingImages(false);
                    return;
                }
                
                // 根据凭证类型优先获取对应的图片URLs
                let urls: string[] = [];
                if (currentProofType === 'delivery') {
                    // 查看签收凭证 - 优先获取POD图片
                    urls = packageDetail.podImageS3UrlsJson || [];
                } else if (currentProofType === 'failure') {
                    // 查看失败凭证 - 优先获取异常图片
                    urls = packageDetail.exceptionImageS3UrlsJson || [];
                } else {
                    // 默认情况：POD图片和异常图片都尝试获取
                    urls = packageDetail.podImageS3UrlsJson || packageDetail.exceptionImageS3UrlsJson || [];
                }
                showImages(urls);
            },
            onError: (error) => {
                console.error('Failed to load package detail:', error);
                setLoadingImages(false);
            }
        }
    );

    // 格式化日期为API需要的格式
    const formatDateForAPI = (date: Date): string => {
        return date.toISOString().split('T')[0];
    };

    // 分页状态
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [allPackages, setAllPackages] = useState<DspPackageData[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const scrollPositionRef = React.useRef<number>(0);
    const shouldRestoreScrollRef = React.useRef<boolean>(false);

    /**
     * 构建查询参数
     */
    const buildQueryVariables = useCallback((options: {
        status?: PackageStatus;
        trackingNumber?: string | null;
        page?: number;
    } = {}) => {
        return {
            filter: {
                taskDate: date || formatDateForAPI(new Date()),
                driverId: driverId ? parseInt(driverId) : null,
                status: options.status ?? activeTab,
                trackingNumber: options.trackingNumber ?? (searchValue.trim() || null),
                warehouseId: warehouseIdVar
            },
            pageRequest: {
                page: options.page ?? 0,
                size: pageSize
            }
        };
    }, [date, driverId, activeTab, searchValue, pageSize, warehouseIdVar]);

    /**
     * 重置分页状态
     */
    const resetPagination = useCallback(() => {
        setCurrentPage(0);
        setAllPackages([]);
        setHasMore(true);
        setIsLoadingMore(false);
        setIsInitialLoading(true);
        setIsRefetching(false);
        scrollPositionRef.current = 0;
        shouldRestoreScrollRef.current = false;
    }, []);

    // GraphQL查询获取包裹数据 - 优先网络，失败时使用缓存
    const {data: packagesData, loading, error, refetch, fetchMore, networkStatus} = useQuery<DspDriverPackagesResponse>(
        GET_DSP_DRIVER_PACKAGES,
        {
            variables: buildQueryVariables(),
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'network-only', // 优先发起网络请求
            nextFetchPolicy: 'network-only', // 后续请求也优先网络
            onError: (error) => {
                console.error('Network request failed:', error);
                // 网络请求失败时，标记需要从缓存获取数据
                setNetworkFailed(true);
            }
        }
    );

    // 缓存查询状态
    const [networkFailed, setNetworkFailed] = useState(false);
    const [cacheData, setCacheData] = useState<DspDriverPackagesResponse | null>(null);

    // 当网络失败时，尝试从缓存获取数据
    useEffect(() => {
        if (networkFailed) {
            console.log('Network failed, attempting to load from cache...');
            try {
                const cachedResult = apolloClient.readQuery<DspDriverPackagesResponse>({
                    query: GET_DSP_DRIVER_PACKAGES,
                    variables: buildQueryVariables()
                });

                if (cachedResult?.getDspDriverPackages) {
                    console.log('Successfully loaded data from cache after network failure');
                    setCacheData(cachedResult);
                } else {
                    console.log('No cached data available');
                    setCacheData(null);
                }
            } catch (cacheError) {
                console.error('Failed to read from cache:', cacheError);
                setCacheData(null);
            }

            setNetworkFailed(false); // 重置状态，避免重复查询
            setIsInitialLoading(false);
            setIsRefetching(false);
        }
    }, [networkFailed, apolloClient, buildQueryVariables]);

    /**
     * 执行查询（refetch）
     */
    const executeQuery = useCallback(async (options: {
        status?: PackageStatus;
        trackingNumber?: string | null;
        page?: number;
        resetPaginationState?: boolean;
    } = {}) => {
        if (options.resetPaginationState !== false) {
            setIsRefetching(true);
            // 不立即重置数据，保持现有数据显示加载状态
            setCurrentPage(0);
            setHasMore(true);
            setIsLoadingMore(false);
            scrollPositionRef.current = 0;
            shouldRestoreScrollRef.current = false;
        }
        try {
            const variables = buildQueryVariables(options);
            const result = await refetch(variables);
            if (options.resetPaginationState !== false) {
                setIsRefetching(false);
            }
            return result;
        } catch (error) {
            console.error('Failed to execute query:', error);
            // 确保出错时也退出加载状态
            if (options.resetPaginationState !== false) {
                setIsRefetching(false);
                setIsInitialLoading(false);
            }
            throw error;
        }
    }, [refetch, buildQueryVariables]);

    // 当 searchValue 或 activeTab 变化时，自动重新查询（但避免在 handleSearch 和 handleTabChange 中重复查询）
    const isRefetchingRef = React.useRef(false);
    const hasInitializedRef = React.useRef(false); // 跟踪是否已经执行过初始化查询

    useEffect(() => {
        // 如果正在手动 refetch（在 handleSearch 或 handleTabChange 中），则跳过自动查询
        if (isRefetchingRef.current) {
            isRefetchingRef.current = false;
            return;
        }

        // 确保有必要的参数再执行查询
        if (activeTab && (driverId !== undefined || date !== undefined)) {
            // 如果是初始化查询（还未执行过），标记为已初始化并执行查询
            if (!hasInitializedRef.current) {
                hasInitializedRef.current = true;
                executeQuery().catch((error) => {
                    console.error('Failed to initial load packages:', error);
                    setIsInitialLoading(false);
                    setIsRefetching(false);
                });
            } else {
                // 非初始化查询，需要重新查询数据
                executeQuery();
            }
        }
    }, [activeTab, searchValue, date, driverId]); // 移除 executeQuery 依赖，避免无限循环

    // 当从详情页面返回时，确保数据正确重新加载
    const hasHandledNavigationRef = useRef(false);
    useEffect(() => {
        const tabTypeFromUrl = searchParams.get('tabType');
        // 只有当URL中包含tabType参数且还未处理过导航时，才认为是POD页面返回，需要强制重新加载
        if (date && driverId && tabTypeFromUrl && !hasHandledNavigationRef.current) {
            console.log('Page navigated back from POD page, forcing data reload for date:', date, 'driverId:', driverId, 'tabType:', tabTypeFromUrl);
            hasHandledNavigationRef.current = true;

            // 重置状态并重新查询
            setIsInitialLoading(true);
            setAllPackages([]);
            setCurrentPage(0);
            setHasMore(true);
            setIsLoadingMore(false);
            setSearchValue('');
            // 设置正确的tab状态
            const targetTab = tabTypeFromUrl as PackageStatus;
            if (Object.values(PackageStatus).includes(targetTab)) {
                setActiveTab(targetTab);
            } else {
                setActiveTab(PackageStatus.DELIVERING);
            }

            // 强制重新查询，使用 network-only 确保获取最新数据
            setTimeout(() => {
                refetch({
                    ...buildQueryVariables(),
                    fetchPolicy: 'network-only' as const
                });
            }, 100);
        }
    }, [date, driverId]); // 只在 date 或 driverId 变化时触发，避免 searchParams 变化导致重复执行

    // 当数据加载完成时，设置数据（用于网络数据和缓存数据）
    useEffect(() => {
        console.log('Data effect triggered:', {
            hasPackagesData: !!packagesData?.getDspDriverPackages,
            loading,
            hasCacheData: !!cacheData?.getDspDriverPackages,
            isInitialLoading,
            isRefetching,
            allPackagesLength: allPackages.length
        });

        // 优先处理网络数据 - 当网络请求成功时，清除缓存数据状态
        if (packagesData?.getDspDriverPackages && !loading) {
            console.log(`Network data loaded successfully, setting loading states to false`);
            // 网络请求成功时清除缓存数据，避免冲突
            if (cacheData) {
                setCacheData(null);
            }

            const responsePage = packagesData.getDspDriverPackages.page;
            const responsePackages = packagesData.getDspDriverPackages.packages;

            if (responsePage === 0) {
                setAllPackages(responsePackages);
                setHasMore(packagesData.getDspDriverPackages.hasNext);
                setCurrentPage(0);
                setIsInitialLoading(false);
                setIsRefetching(false);
            }
            // 处理后续页数据
            else if (allPackages.length === 0 && responsePackages.length > 0) {
                setAllPackages(responsePackages);
                setHasMore(packagesData.getDspDriverPackages.hasNext);
                setCurrentPage(responsePage);
                setIsInitialLoading(false);
                setIsRefetching(false);
            }
            // 其他情况（fetchMore 加载的后续页）不在这里处理，由 loadMore 函数处理
        }
        // 处理缓存数据（当网络失败时）
        else if (cacheData?.getDspDriverPackages && !packagesData) {
            const responsePage = cacheData.getDspDriverPackages.page;
            const responsePackages = cacheData.getDspDriverPackages.packages;

            console.log(`Using cached data after network failure: ${responsePackages.length} packages, setting loading states to false`);

            if (responsePage === 0) {
                setAllPackages(responsePackages);
                setHasMore(cacheData.getDspDriverPackages.hasNext);
                setCurrentPage(0);
                setIsInitialLoading(false);
                setIsRefetching(false);
            }
        }

        // 如果网络请求完成但没有数据且没有缓存数据，确保退出加载状态
        if (!loading && !packagesData?.getDspDriverPackages && !cacheData?.getDspDriverPackages) {
            console.log('No data available, setting loading states to false');
            setIsInitialLoading(false);
            setIsRefetching(false);
        }

        // 额外保护：如果有实际数据，强制退出加载状态
        if (allPackages.length > 0 && (isInitialLoading || isRefetching)) {
            console.log('Have actual data but still in loading state, forcing exit loading state');
            setIsInitialLoading(false);
            setIsRefetching(false);
        }
    }, [packagesData, cacheData, loading, isRefetching, allPackages.length]);

    // 转换GraphQL数据为本地数据格式
    const convertToLocalData = (data: DspPackageData[]): LocalPackageData[] => {
        return data.map((pkg, index) => ({
            ...pkg,
            id: pkg.orderId || `${index}`,
            status: pkg.status as PackageStatus,
            address: pkg.recipientAddress,
            deliveryTime: pkg.deliveredAt ? formatTimestampToSecondsTime(pkg.deliveredAt) : undefined,
            failureReason: pkg.exceptionReasonCode,
            operationTime: pkg.failAt ? formatTimestampToSecondsTime(pkg.failAt) : undefined,
            deliveryAttempts: pkg.deliveryAttempts || 0,
            orderNote: pkg.orderNote
        }));
    };

    // 获取实际数据（使用累积的所有数据）
    const actualPackageData = useMemo(() => {
        if (allPackages.length > 0) {
            return convertToLocalData(allPackages);
        }
        
        // 如果查询失败或没有数据，返回空数组
        return [];
    }, [allPackages]);

    // 当数据更新后，恢复滚动位置（用于无限滚动时保持位置）
    React.useLayoutEffect(() => {
        if (shouldRestoreScrollRef.current && scrollContainerRef.current && scrollPositionRef.current > 0) {
            const scrollContainer = scrollContainerRef.current;
            const savedPosition = scrollPositionRef.current;

            // 立即恢复滚动位置，避免闪烁
            scrollContainer.scrollTo({
                top: savedPosition,
                behavior: 'auto'
            });

            // 使用更稳定的方式确保位置恢复
            const restoreScroll = () => {
                if (scrollContainer && scrollPositionRef.current > 0) {
                    scrollContainer.scrollTo({
                        top: scrollPositionRef.current,
                        behavior: 'auto'
                    });
                }
            };

            // 延迟执行以确保DOM更新完成
            setTimeout(restoreScroll, 0);
            requestAnimationFrame(restoreScroll);

            shouldRestoreScrollRef.current = false;
        }
    }, [allPackages.length]);

    // 监听页面切换时保持滚动位置，并在组件卸载时清理状态
    React.useEffect(() => {
        const handleBeforeUnload = () => {
            if (scrollContainerRef.current) {
                scrollPositionRef.current = scrollContainerRef.current.scrollTop;
            }
        };

        // 当组件重新挂载时（比如tab切换），恢复滚动位置
        const restoreOnMount = () => {
            if (scrollContainerRef.current && scrollPositionRef.current > 0 && !shouldRestoreScrollRef.current) {
                requestAnimationFrame(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTo({
                            top: scrollPositionRef.current,
                            behavior: 'auto'
                        });
                    }
                });
            }
        };

        restoreOnMount();

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // 组件卸载时重置初始化标志，避免快速导航时的状态污染
            hasInitializedRef.current = false;
        };
    }, [activeTab, driverId, date]); // 当这些关键参数变化时重新执行

    // 获取各状态数量（从后端返回，使用最新查询结果）
    const tabCountsFromBackend = useMemo(() => {
        if (packagesData?.getDspDriverPackages) {
            return {
                [PackageStatus.DELIVERING]: packagesData.getDspDriverPackages.deliveringCount,
                [PackageStatus.DELIVERED]: packagesData.getDspDriverPackages.deliveredCount,
                [PackageStatus.FAILED]: packagesData.getDspDriverPackages.failedCount,
                [PackageStatus.RETURNED]: packagesData.getDspDriverPackages.returnedCount
            };
        }
        return {
            [PackageStatus.DELIVERING]: 0,
            [PackageStatus.DELIVERED]: 0,
            [PackageStatus.FAILED]: 0,
            [PackageStatus.RETURNED]: 0
        };
    }, [packagesData]);

    // 加载更多数据
    const loadMore = useCallback(async () => {
        if (!hasMore || loading || isLoadingMore || isInitialLoading || isRefetching) {
            return;
        }

        const container = scrollContainerRef.current;
        if (!container) return;

        // 保存当前滚动位置
        const currentScrollTop = container.scrollTop;
        scrollPositionRef.current = currentScrollTop;
        console.log(`[loadMore] Saving scroll position before loading page ${currentPage + 1}: ${scrollPositionRef.current}`);

        setIsLoadingMore(true);
        const nextPage = currentPage + 1;

        try {
            const variables = buildQueryVariables({
                page: nextPage
            });
            const result = await fetchMore({
                variables,
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.getDspDriverPackages) {
                        return prev;
                    }
                    return fetchMoreResult;
                }
            });

            if (result.data?.getDspDriverPackages) {
                const newPackages = result.data.getDspDriverPackages.packages;

                if (newPackages.length > 0) {
                    // 标记需要恢复滚动位置
                    shouldRestoreScrollRef.current = true;

                    // 使用函数式更新，确保数据追加正确
                    setAllPackages(prev => [...prev, ...newPackages]);

                    setHasMore(result.data.getDspDriverPackages.hasNext);
                    setCurrentPage(nextPage);
                }
            }
        } catch (error) {
            console.error('Failed to load more packages:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMore, loading, isLoadingMore, currentPage, fetchMore, date, driverId, activeTab, pageSize, isInitialLoading, isRefetching]);

    // 监听URL参数变化，同步tab状态
    useEffect(() => {
        const tabTypeFromUrl = searchParams.get('tabType') as PackageStatus;
        if (tabTypeFromUrl && tabTypeFromUrl !== activeTab) {
            setActiveTab(tabTypeFromUrl);
        }
    }, [searchParams]);

    // 初始化查询由上面的 useEffect 处理，这里不需要额外的初始化逻辑

    // 当driverId或date变化时（比如URL参数变化），重新查询数据
    useEffect(() => {
        // 避免在组件初始化时重复查询
        if (activeTab && (driverId !== undefined || date !== undefined)) {
            // 重置分页状态
            setCurrentPage(0);
            setAllPackages([]);
            setHasMore(true);
            setIsLoadingMore(false);
            setIsInitialLoading(true);
            setIsRefetching(false);
            scrollPositionRef.current = 0;
            shouldRestoreScrollRef.current = false;

            // 滚动到顶部
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    top: 0,
                    behavior: 'auto'
                });
            }

            // 重新查询数据
            executeQuery().catch((error) => {
                console.error('Failed to refetch packages:', error);
                setIsInitialLoading(false);
                setIsRefetching(false);
            });
        }
    }, [driverId, date, activeTab]); // 添加 activeTab 依赖，确保状态同步

    // 加载异常原因字典
    useEffect(() => {
        const loadExceptionDictionary = async () => {
            try {
                const dictionaries = await getExceptionDictionaryAll();
                const dictMap = new Map<string, ExceptionDictionaryType>();
                dictionaries.forEach(dict => {
                    dictMap.set(dict.exceptionReasonCode, dict);
                });
                setExceptionDictMap(dictMap);
            } catch (error) {
                console.error('Failed to load exception dictionary:', error);
            }
        };
        loadExceptionDictionary();
    }, []);

    // 根据exceptionReasonCode获取多语言异常原因文案
    const getExceptionReasonText = useCallback((exceptionReasonCode: string | undefined): string => {
        if (!exceptionReasonCode) return '';
        
        const exception = exceptionDictMap.get(exceptionReasonCode);
        if (!exception) return exceptionReasonCode; // 如果找不到字典，返回原始code

        const currentLang = getCurrentLanguage();
        
        // 根据当前语言返回对应的文案
        if (currentLang.includes('zh')) {
            return exception.secondaryTypeZh || exceptionReasonCode;
        } else if (currentLang.includes('es')) {
            return exception.secondaryTypeEs || exception.secondaryTypeEn || exceptionReasonCode;
        } else {
            return exception.secondaryTypeEn || exceptionReasonCode;
        }
    }, [exceptionDictMap]);

    // 滚动监听，实现无限滚动（带防抖）
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        let scrollTimer: NodeJS.Timeout | null = null;
        let isScrolling = false;
        let isRestoringScroll = false;

        const handleScroll = () => {
            // 如果正在恢复滚动位置，不触发加载
            if (shouldRestoreScrollRef.current || isRestoringScroll) {
                return;
            }

            if (scrollTimer) {
                clearTimeout(scrollTimer);
            }

            scrollTimer = setTimeout(() => {
                if (isScrolling || isRestoringScroll) return;

                const scrollTop = container.scrollTop;
                const scrollHeight = container.scrollHeight;
                const clientHeight = container.clientHeight;
                const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

                if (isNearBottom && hasMore && !loading && !isLoadingMore && !isInitialLoading && !isRefetching) {
                    isScrolling = true;
                    loadMore().finally(() => {
                        // 延迟重置标志，确保滚动位置恢复完成
                        setTimeout(() => {
                            isScrolling = false;
                        }, 200);
                    });
                }
            }, 150);
        };

        // 监听滚动位置恢复开始和结束
        const handleScrollRestoreStart = () => {
            isRestoringScroll = true;
            setTimeout(() => {
                isRestoringScroll = false;
            }, 300); // 300ms后认为滚动恢复完成
        };

        if (shouldRestoreScrollRef.current) {
            handleScrollRestoreStart();
        }

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (scrollTimer) {
                clearTimeout(scrollTimer);
            }
        };
    }, [hasMore, loading, isLoadingMore, loadMore, isInitialLoading, isRefetching]);

    // 不再需要前端过滤，数据由后端查询返回
    const filteredData = actualPackageData;

    // 使用后端返回的数量
    const tabCounts = tabCountsFromBackend;

    // Tab配置
    const tabs: TabConfig[] = [
        {
            key: PackageStatus.DELIVERING,
            title: `${t('pages.dsp.operationalDataParticulars.tabs.delivering')}(${tabCounts[PackageStatus.DELIVERING]})`,
            count: tabCounts[PackageStatus.DELIVERING]
        },
        {
            key: PackageStatus.DELIVERED,
            title: `${t('pages.dsp.operationalDataParticulars.tabs.delivered')}(${tabCounts[PackageStatus.DELIVERED]})`,
            count: tabCounts[PackageStatus.DELIVERED]
        },
        {
            key: PackageStatus.FAILED,
            title: `${t('pages.dsp.operationalDataParticulars.tabs.failed')}(${tabCounts[PackageStatus.FAILED]})`,
            count: tabCounts[PackageStatus.FAILED]
        },
        {
            key: PackageStatus.RETURNED,
            title: `${t('pages.dsp.operationalDataParticulars.tabs.returned')}(${tabCounts[PackageStatus.RETURNED]})`,
            count: tabCounts[PackageStatus.RETURNED]
        }
    ];

    const handleSearch = async (value: string) => {
        setSearchValue(value);
        // 标记正在手动 refetch，避免 useEffect 重复查询
        isRefetchingRef.current = true;
        // 调用接口查询
        await executeQuery({
            trackingNumber: value.trim() || null
        });
    };

    const handleTabChange = async (key: string) => {
        const newTab = key as PackageStatus;
        setActiveTab(newTab);

        // 更新URL参数中的tabType
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tabType', newTab);
        setSearchParams(newSearchParams);

        // 标记正在手动 refetch，避免 useEffect 重复查询
        isRefetchingRef.current = true;
        try {
            // 切换tab时重新查询，确保使用最新的 searchValue
            await executeQuery({
                status: newTab
            });
        } catch (error) {
            console.error('Failed to change tab:', error);
            // 确保出错时重置标记
            isRefetchingRef.current = false;
        }
    };

    const handleViewDetail = (trackingNumber: string) => {
        // 构建返回参数，包含当前tab状态
        const returnParams = new URLSearchParams();
        returnParams.set('tabType', activeTab);
        if (driverId) returnParams.set('driverId', driverId);
        if (date) returnParams.set('date', date);
        
        // 根据当前tab状态跳转到不同页面
        if (activeTab === PackageStatus.DELIVERING || activeTab === PackageStatus.RETURNED) {
            // 派送中和失败已退回跳转到详情页
            navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_DETAIL.replace(':trackingNumber', trackingNumber)}?${returnParams.toString()}`);
        } else if (activeTab === PackageStatus.DELIVERED) {
            // 已妥投跳转到POD页面
            navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_POD.replace(':trackingNumber', trackingNumber)}?${returnParams.toString()}`);
        } else if (activeTab === PackageStatus.FAILED) {
            // 派送失败跳转到标记失败页面
            navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_MARK_FAIL.replace(':trackingNumber', trackingNumber)}?${returnParams.toString()}`);
        }
    };

    const handleViewProof = useCallback((trackingNumber: string, proofType: 'delivery' | 'failure') => {
        const packageData = actualPackageData.find(pkg => pkg.trackingNumber === trackingNumber);

        if (!packageData) {
            console.error('Package not found:', trackingNumber);
            return;
        }

        // 保存当前凭证类型，用于 getPackageDetail 回调
        setCurrentProofType(proofType);

        let s3Keys: string[] = [];

        if (proofType === 'delivery') {
            // 查看签收凭证 - 获取POD图片
            s3Keys = packageData.podImageS3KeysJson || [];
        } else if (proofType === 'failure') {
            // 查看失败凭证 - 获取异常图片
            s3Keys = packageData.exceptionImageS3KeysJson || [];
        }

        setLoadingImages(true);
        
        if (s3Keys.length > 0) {
            getImageUrls({ variables: { s3Keys } });
        } else {
            getPackageDetail({ variables: { trackingNumber } });
        }
    }, [actualPackageData, getImageUrls, getPackageDetail]);

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* 搜索栏 */}
            <DspSearchBar onSearch={handleSearch}/>

            {/* Tab切换 */}
            <div className="bg-white border-b border-gray-200">
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                >
                    {tabs.map(tab => (
                        <Tabs.Tab
                            key={tab.key}
                            title={tab.title}
                        />
                    ))}
                </Tabs>
            </div>

            {/* 内容区域 */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto p-3"
                style={{
                    WebkitOverflowScrolling: 'touch',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}
            >
                {/* 预设固定数量的占位符，保持页面高度稳定 */}
                {/* 显示骨架屏的条件：初始加载中 或者 重新获取第一页数据中 且 没有实际数据 */}
                {(isInitialLoading || (isRefetching && currentPage === 0)) && allPackages.length === 0 ? (
                    <div className="space-y-3">
                        {/* 预设10个骨架屏占位符，对应pageSize */}
                        {Array.from({ length: pageSize }, (_, i) => (
                            <Card key={`skeleton-${i}`} className="rounded-lg shadow-sm mb-3">
                                <div className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="space-y-2">
                                                {/* 模拟包裹信息占位符 */}
                                                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                                                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
                                            </div>
                                        </div>
                                        <div className="ml-4 flex-shrink-0">
                                            {/* 模拟箭头图标占位符 */}
                                            <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
                                        {/* 模拟按钮占位符 */}
                                        <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredData.length > 0 ? (
                            <>
                                {filteredData.map(pkg => (
                                    <PackageCard
                                        key={pkg.id}
                                        package={pkg}
                                        onViewDetail={handleViewDetail}
                                        onViewProof={handleViewProof}
                                        getExceptionReasonText={getExceptionReasonText}
                                        loadingImages={loadingImages}
                                    />
                                ))}
                                {(loading || isLoadingMore) && currentPage > 0 && (
                                    <div className="text-center py-4 text-gray-500">
                                        {t('pages.dsp.operationalDataParticulars.loading')}
                                    </div>
                                )}
                                {!hasMore && filteredData.length > 0 && (
                                    <div className="text-center py-4 text-gray-500">
                                        {t('pages.dsp.operationalDataParticulars.noMoreData')}
                                    </div>
                                )}
                            </>
                        ) : error ? (
                            <div className="text-center mt-8">
                                <ErrorBlock
                                    title={t('pages.dsp.operationalDataParticulars.networkError')}
                                    description={t('pages.dsp.operationalDataParticulars.networkErrorDescription')}
                                    image="networkError"
                                    style={{ '--image-height': '100px' }}
                                />
                                <Button
                                    color="primary"
                                    className="mt-4"
                                    onClick={async () => {
                                        try {
                                            await refetch(buildQueryVariables());
                                        } catch (error) {
                                            console.error('Failed to refetch:', error);
                                        }
                                    }}
                                >
                                    {t('pages.dsp.operationalDataParticulars.retry')}
                                </Button>
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 mt-8">
                                {searchValue ? t('pages.dsp.operationalDataParticulars.noMatchingPackages') : t('pages.dsp.operationalDataParticulars.noData')}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 图片查看器 */}
            <ImageViewer.Multi
                key={imageViewerKey}
                images={currentImages}
                visible={imageViewerVisible}
                defaultIndex={0}
                onClose={() => setImageViewerVisible(false)}
                getContainer={null}
                imageRender={(image) => (
                    <img src={image} style={{ width: '90vw', height: '90vh', objectFit: 'contain' }} />
                )}
            />
        </div>
    );
};

const DspOperationalDataParticularsPage: React.FC = () => {
    const {isOnline} = useNetwork();
    const navigate = useNavigate();
    const params = useParams<{ driverId: string; }>();
    const [searchParams] = useSearchParams();

    // 从URL参数获取数据
    const driverId = params.driverId;
    const dateParam = searchParams.get('date');
    const title = t('pages.dsp.operationalDataParticulars.title');

    const handleBack = () => {
        // 返回时传递当前日期参数，保持页面状态
        const urlParams = new URLSearchParams(window.location.search);
        const dateParam = urlParams.get('date');
        const searchParams = new URLSearchParams();
        if (dateParam) {
            searchParams.set('date', dateParam);
        }
        navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL}?${searchParams.toString()}`);
    };

    return (
        <div className="flex flex-col h-screen">
            {/* 导航栏 */}
            <div className="flex-none sticky top-0 z-10 bg-white shadow-sm">
                <NavBar
                    onBack={handleBack}
                    backIcon={<LeftOutline/>}
                >
                    {title}
                </NavBar>
            </div>

            {/* 扫码组件 */}
            {isOnline && <BarCodeScanner showManualInputIcon={false}/>}

            {/* 主体内容 */}
            <div className="flex-1 overflow-hidden">
                <Body driverId={driverId} date={dateParam}/>
            </div>
        </div>
    );
};

export default DspOperationalDataParticularsPage;